import React, { useEffect, useRef, useState } from 'react';
import { audioSourceType, MediaInfo } from './types';
import { AppIcon } from '../../../../../components/icons/AppIcon';
import { ChatAvatar } from '../ChatAvatar';
import { formatRecordingTime, isValidMediaDuration } from '../../../lib/audioRecorder';

interface AudioMediaProps {
    media: MediaInfo;
    isMe?: boolean;
    senderName?: string;
    senderAvatar?: string;
    chatPlatform?: 'whatsapp' | 'instagram';
}

const WAVEFORM_BANDS = [
    25, 45, 60, 40, 50, 75, 55, 35, 65, 95, 80, 50, 70, 100, 85,
    60, 50, 75, 90, 85, 65, 80, 95, 100, 75, 55, 40, 35, 25, 20,
];
const PLAYBACK_SPEEDS = [1, 1.5, 2] as const;

export const AudioMedia: React.FC<AudioMediaProps> = ({
    media,
    isMe,
    senderName,
    senderAvatar,
    chatPlatform = 'whatsapp',
}) => {
    const transcription = media.transcription?.trim() || '';
    const audioRef = useRef<HTMLAudioElement>(null);
    const [hasError, setHasError] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(media.duration || 0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const sourceType = audioSourceType(media.url, media.mimetype);
    const fg = isMe ? 'text-chat-bubble-me-fg' : 'text-chat-bubble-them-fg';

    const senderAvatarEl = (
        <ChatAvatar
            name={senderName || 'W'}
            avatar={senderAvatar}
            size="md"
            showChannelIcon
            customBadgeIcon={chatPlatform === 'instagram' ? 'instagram' : 'whatsapp'}
            badgeOnBrand={isMe}
            borderColorClass={isMe ? 'border-transparent' : 'border-border'}
        />
    );

    useEffect(() => {
        setHasError(false);
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
    }, [media.url]);

    const applyDuration = (d: number) => {
        if (isValidMediaDuration(d)) setDuration(d);
    };

    const togglePlay = () => {
        const el = audioRef.current;
        if (!el) return;
        if (!el.paused) el.pause();
        else void el.play().catch((error: unknown) => {
            if (audioRef.current !== el) return;
            setIsPlaying(false);
            // Pausar ou trocar a conversa pode interromper um play ainda pendente.
            if (!(error instanceof DOMException && error.name === 'AbortError')) setHasError(true);
        });
    };

    const handleTimeUpdate = () => {
        const el = audioRef.current;
        if (!el) return;
        setCurrentTime(el.currentTime);
        if (isValidMediaDuration(el.duration)) {
            setDuration(el.duration);
            setProgress((el.currentTime / el.duration) * 100);
        }
    };

    const handleLoadedMetadata = () => {
        const el = audioRef.current;
        if (!el) return;
        if (el.duration === Infinity) {
            el.currentTime = 1e101;
            el.addEventListener('timeupdate', function getDuration() {
                if (!audioRef.current) return;
                const d = audioRef.current.duration;
                if (d !== Infinity && isValidMediaDuration(d)) {
                    setDuration(d);
                    audioRef.current.currentTime = 0;
                    audioRef.current.removeEventListener('timeupdate', getDuration);
                }
            });
            return;
        }
        applyDuration(el.duration);
    };

    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        const el = audioRef.current;
        if (!el || !isValidMediaDuration(el.duration)) return;
        const rect = e.currentTarget.getBoundingClientRect();
        el.currentTime = ((e.clientX - rect.left) / rect.width) * el.duration;
    };

    const handleSpeedChange = () => {
        const idx = PLAYBACK_SPEEDS.indexOf(playbackRate as (typeof PLAYBACK_SPEEDS)[number]);
        const next = PLAYBACK_SPEEDS[(idx + 1) % PLAYBACK_SPEEDS.length];
        setPlaybackRate(next);
        if (audioRef.current) audioRef.current.playbackRate = next;
    };

    const transcriptionEl = transcription ? (
        <p className={`mt-2 text-sm leading-relaxed whitespace-pre-wrap ${hasError ? 'text-foreground/70' : 'ml-12'}`}>
            {transcription}
        </p>
    ) : null;

    if (hasError) {
        return (
            <div className="p-1.5 w-60 max-w-full min-w-0 pb-3">
                <div className="flex items-center gap-3">
                    {senderAvatarEl}
                    <span className={`text-xs italic ${isMe ? 'text-chat-bubble-me-fg/80' : 'text-chat-bubble-them-fg/80'}`}>
                        Áudio indisponível
                    </span>
                </div>
                {transcriptionEl}
            </div>
        );
    }

    return (
        <div className="p-1.5 w-60 max-w-full min-w-0 pb-3">
            <div className="flex items-center gap-3">
                {senderAvatarEl}
                <div className={`flex min-w-0 flex-1 items-start gap-3 px-2 py-2 bg-transparent ${fg}`}>
                    <button type="button" onClick={togglePlay} className="flex-shrink-0 focus:outline-none hover:opacity-80 transition-opacity mt-0.5">
                        <AppIcon name={isPlaying ? 'pause' : 'play'} className="w-5 h-5 fill-current" />
                    </button>
                    <div className="flex flex-col flex-1 gap-1 w-full overflow-hidden">
                        <div className="flex items-center gap-3 w-full">
                            <div className="flex-1 flex items-center justify-between gap-[2px] h-6 cursor-pointer" onClick={handleSeek}>
                                {WAVEFORM_BANDS.map((peak, index) => {
                                    const bandRatio = index / WAVEFORM_BANDS.length;
                                    const isPlayed = progress > bandRatio * 100;
                                    const fill = isPlayed ? (isMe ? 'bg-chat-bubble-me-fg' : 'bg-primary') : (isMe ? 'bg-chat-bubble-me-fg/30' : 'bg-primary/20');
                                    const pulse = isPlaying && isPlayed && progress - bandRatio * 100 < 15 ? 'animate-pulse' : '';
                                    return <div key={index} className={`flex-1 rounded-full transition-colors duration-150 ${fill} ${pulse}`} style={{ height: `${Math.max(20, peak)}%` }} />;
                                })}
                            </div>
                            <button type="button" onClick={handleSpeedChange} className={`flex-shrink-0 focus:outline-none hover:opacity-80 transition-opacity min-w-[28px] text-center rounded px-1.5 py-0.5 text-[10px] font-bold ${isMe ? 'bg-chat-bubble-me-fg/20 text-chat-bubble-me-fg' : 'bg-primary/10 text-primary'}`}>
                                {playbackRate}x
                            </button>
                        </div>
                        <div className={`text-[11px] font-medium leading-none ${isMe ? 'text-chat-bubble-me-fg/80' : 'text-chat-bubble-them-fg/80'}`}>
                            {formatRecordingTime(Math.floor(isPlaying || currentTime > 0 ? currentTime : duration))}
                        </div>
                    </div>
                    <audio ref={audioRef} className="hidden" onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onDurationChange={() => applyDuration(audioRef.current?.duration ?? 0)} onEnded={() => { setIsPlaying(false); setProgress(0); setCurrentTime(0); }} onError={() => setHasError(true)} preload="metadata">
                        {sourceType ? <source src={media.url} type={sourceType} /> : null}
                        <source src={media.url} />
                    </audio>
                </div>
            </div>
            {transcriptionEl}
        </div>
    );
};

export const AudioError: React.FC = () => (
    <div className="p-3 italic text-sm bg-black/5 rounded-lg border border-border flex items-center gap-2">
        <AppIcon name="alert-triangle" className="w-4 h-4 text-foreground/70" />
        <span className="text-foreground/70">Áudio indisponível</span>
    </div>
);
